import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import Swal from "sweetalert2";
import StatusChip from "components/StatusChip";
import { API_BASE } from "config/api";

// Define prop types for row data
const rowPropTypes = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serial: PropTypes.number,
  churchName: PropTypes.string,
  branchName: PropTypes.string,
  address: PropTypes.string,
  status: PropTypes.string,
  memberCount: PropTypes.number,
  incomeTotal: PropTypes.number,
  attendanceToday: PropTypes.number,
  createdAt: PropTypes.string,
  originalData: PropTypes.object,
});

const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = {
  value: PropTypes.any.isRequired,
};

const createChurchCell = (church) => {
  const ChurchCell = ({ value }) => (
    <Box display="flex" alignItems="center" gap={1.5}>
      <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>{value?.charAt(0)}</Avatar>
      <Box>
        <Typography variant="button" fontWeight={600} display="block">
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {church?.email || "church@example.com"}
        </Typography>
      </Box>
    </Box>
  );
  ChurchCell.propTypes = {
    value: PropTypes.string.isRequired,
  };
  return ChurchCell;
};

const BranchCell = ({ value, row }) => (
  <Box textAlign="left">
    <Typography variant="button" fontWeight={600} display="block">
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {row.original.address || "No address set"}
    </Typography>
  </Box>
);
BranchCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({
    original: rowPropTypes,
  }).isRequired,
};

const StatusCell = ({ value }) => <StatusChip value={value} />;
StatusCell.propTypes = {
  value: PropTypes.string.isRequired,
};

const NumberCell = ({ value }) => (
  <Typography variant="button" fontWeight={600}>
    {value ?? 0}
  </Typography>
);
NumberCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
NumberCell.defaultProps = {
  value: 0,
};

const MoneyCell = ({ value }) => (
  <Typography variant="button" fontWeight={600}>
    GHS {Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
  </Typography>
);
MoneyCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
MoneyCell.defaultProps = {
  value: 0,
};

const ActionsCell = ({ row, handleView, handleEdit, handleDelete }) => (
  <Stack direction="row" spacing={1} justifyContent="center">
    <Button
      size="small"
      variant="outlined"
      color="info"
      startIcon={<VisibilityIcon fontSize="small" />}
      onClick={() => handleView(row.original)}
    >
      View
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="primary"
      startIcon={<EditIcon fontSize="small" />}
      onClick={() => handleEdit(row.original)}
    >
      Edit
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon fontSize="small" />}
      onClick={() => handleDelete(row.original)}
    >
      Delete
    </Button>
  </Stack>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: rowPropTypes,
  }).isRequired,
  handleView: PropTypes.func.isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

export default function useBranchData() {
  const { user, church } = useAuth();

  const ChurchCell = createChurchCell(church);

  const [branchData, setBranchData] = useState({
    columns: [
      {
        Header: "SN",
        accessor: "serial",
        align: "center",
        width: "5%",
        Cell: SerialCell,
      },
      {
        Header: "Church",
        accessor: "churchName",
        align: "left",
        width: "30%",
        Cell: ChurchCell,
      },
      {
        Header: "Branch",
        accessor: "branchName",
        align: "left",
        width: "25%",
        Cell: BranchCell,
      },
      {
        Header: "Status",
        accessor: "status",
        align: "center",
        width: "10%",
        Cell: StatusCell,
      },
      {
        Header: "Members",
        accessor: "memberCount",
        align: "center",
        width: "10%",
        Cell: NumberCell,
      },
      {
        Header: "Income",
        accessor: "incomeTotal",
        align: "center",
        width: "12%",
        Cell: MoneyCell,
      },
      {
        Header: "Today",
        accessor: "attendanceToday",
        align: "center",
        width: "8%",
        Cell: NumberCell,
      },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "25%",
        Cell: (props) => (
          <ActionsCell
            {...props}
            handleView={handleView}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
          />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const navigate = useNavigate();

  const handleView = (branch) => {
    navigate(`/branches/${branch.id}`);
  };

  const handleEdit = (branch) => {
    setSelectedBranch(branch);
    setEditModalOpen(true);
  };

  const handleDelete = (branch) => {
    setSelectedBranch(branch);
    setDeleteModalOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const url = selectedBranch.id
        ? `${API_BASE}/branches/${selectedBranch.id}`
        : `${API_BASE}/branches`;

      const method = selectedBranch.id ? "PUT" : "POST";

      const payload = {
        name: selectedBranch.branchName,
        address: selectedBranch.address,
        phone: selectedBranch.phone,
        status: selectedBranch.status === "active",
      };
      // Only include admin fields on create, and only if a name was provided
      if (!selectedBranch.id && selectedBranch.adminName) {
        payload.admin_name = selectedBranch.adminName;
        if (selectedBranch.adminEmail) payload.admin_email = selectedBranch.adminEmail;
        if (selectedBranch.adminPhone) payload.admin_phone = selectedBranch.adminPhone;
        if (selectedBranch.adminPassword) payload.admin_password = selectedBranch.adminPassword;
      }

      const response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json().catch(() => ({}));
      const adminCreated = result?.admin;
      Swal.fire({
        title: "Success!",
        html: adminCreated
          ? `Branch saved.<br/>Branch admin account created for <strong>${adminCreated.name}</strong> (<strong>${adminCreated.email}</strong>).<br/><small>You can reset their password anytime from <em>Users Account</em>.</small>`
          : "Branch successfully saved.",
        icon: "success",
        confirmButtonText: "OK",
      });

      fetchBranches();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Branch operation failed:", error);
      setEditModalOpen(false);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/branches/${selectedBranch.id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: `Branch, ${selectedBranch.branchName} deleted successfully`,
        icon: "success",
        confirmButtonText: "OK",
      });

      fetchBranches();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
      setBranchData((prev) => ({
        ...prev,
        error: error.message,
      }));
    }
  };

  const handleAddBranch = () => {
    setSelectedBranch({
      id: null,
      branchName: "",
      address: "",
      phone: "",
      status: "active",
      adminName: "",
      adminEmail: "",
      adminPhone: "",
      adminPassword: "",
    });
    setEditModalOpen(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const fetchBranches = async () => {
    try {
      setBranchData((prev) => ({ ...prev, isLoading: true }));
      if (!user || !church) return;

      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/branches/overview`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const branches = result.data || result.branches || result;

      if (!Array.isArray(branches)) {
        throw new Error("API response is not an array");
      }

      setBranchData((prev) => ({
        ...prev,
        rows: branches.map((branch, index) => ({
          id: branch.id,
          serial: index + 1,
          churchName: church.name,
          branchName: branch.name,
          address: branch.address,
          status: branch.status === "active" || branch.status === true ? "active" : "inactive",
          memberCount: branch.member_count,
          incomeTotal: branch.income_total,
          attendanceToday: branch.attendance_today,
          createdAt: branch.created_at,
          originalData: branch,
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Fetch failed:", error);
      setBranchData((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
    }
  };

  useEffect(() => {
    fetchBranches();
  }, [user, church]);

  return {
    ...branchData,
    handleEdit,
    handleDelete,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedBranch,
    setSelectedBranch,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddBranch,
  };
}
